import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send, Sparkles } from "lucide-react";

const WHATSAPP_NUMBER = "919141302057";
const SUPPORT_EMAIL = "care@digitalonbaording.in";
const SUPPORT_PHONE = "+91 9141302057";
const STARTER_PRICE = "₹9,999";

// Static, rule-based knowledge base — no backend calls.
const KB = [
  {
    keywords: ["price", "pricing", "cost", "kitna", "kitne", "₹", "rs", "rate", "fees", "fee"],
    answer:
      `Our starter package is ${STARTER_PRICE} (one-time setup) and includes Website + Mobile App + Payments + AI Assistant. ` +
      `We also offer low monthly maintenance plans. For a custom quote tailored to your business, WhatsApp us at ${SUPPORT_PHONE}.`,
  },
  {
    keywords: ["time", "setup", "fast", "kitne din", "kitni der", "live", "launch", "days"],
    answer:
      `Most shops go live within 2 days. We handle everything: design, mobile app, payments and AI assistant setup — you just share your shop details on WhatsApp.`,
  },
  {
    keywords: ["service", "services", "include", "offer", "kya milta", "kya hai", "features", "feature"],
    answer:
      `Every package includes: ` +
      `\n• Beautifully designed Website` +
      `\n• Branded Mobile App` +
      `\n• Payment Gateway (UPI / Cards / Wallets)` +
      `\n• AI Chat Assistant (24/7, bilingual)` +
      `\n• Live Analytics dashboard` +
      `\n• Free onboarding & WhatsApp support`,
  },
  {
    keywords: ["whatsapp", "phone", "number", "call", "contact", "reach", "support"],
    answer:
      `You can reach us anytime:\n• WhatsApp / Call: ${SUPPORT_PHONE}\n• Email: ${SUPPORT_EMAIL}\nOur team replies within minutes during business hours.`,
  },
  {
    keywords: ["email", "mail"],
    answer: `Email us at ${SUPPORT_EMAIL} — we usually reply within 2 hours.`,
  },
  {
    keywords: ["hindi", "english", "language", "bhasha"],
    answer:
      `Haan ji, hum Hindi aur English dono mein full support dete hain. Aap jis bhasha mein comfortable ho, ussi mein chat karein.`,
  },
  {
    keywords: ["earning", "income", "revenue", "kamai", "profit", "growth", "business"],
    answer:
      `Most of our customers see 3× more orders within 60 days of going online. With WhatsApp Catalogue + your own app + payments, your shop runs 24×7 even when you sleep.`,
  },
  {
    keywords: ["who", "what is", "what's", "company", "about", "kaun", "kya hai"],
    answer:
      `Digital Onboarding is India's friendliest platform for bringing offline shops, CSC centres, restaurants, salons and service providers online — quickly and affordably. Live in 2 days, starting ${STARTER_PRICE}.`,
  },
  {
    keywords: ["payment", "upi", "card", "razorpay", "stripe"],
    answer:
      `Payments are fully integrated — UPI, debit/credit cards, NetBanking and wallets — settled to your bank account daily. You don't manage anything extra.`,
  },
  {
    keywords: ["app", "mobile", "android", "ios"],
    answer:
      `Yes, every customer gets their own branded mobile app — published on Play Store / App Store under your shop's name. Customers can order, pay and chat with you directly.`,
  },
  {
    keywords: ["website", "site", "domain"],
    answer:
      `Your website includes a custom domain (yourshop.com), SSL, 5+ pages, online ordering, product catalogue and SEO setup — all built and managed by our team.`,
  },
  {
    keywords: ["thank", "thanks", "shukriya", "great", "awesome"],
    answer:
      `Khushi se 🙏 Whenever you're ready to start, just WhatsApp us at ${SUPPORT_PHONE} — your shop can be online in 2 days!`,
  },
  {
    keywords: ["hi", "hello", "namaste", "hey"],
    answer:
      `Namaste 🙏 I'm Aria, your Digital Onboarding assistant. I can answer about pricing, services, setup time, payments and more. What would you like to know?`,
  },
];

const FALLBACK =
  `Mujhe ye samajhne mein dikkat ho rahi hai. ` +
  `Aap directly humse baat kar sakte hain:\n` +
  `• WhatsApp / Call: ${SUPPORT_PHONE}\n` +
  `• Email: ${SUPPORT_EMAIL}\n` +
  `Hum 2 ghante ke andar reply karte hain.`;

function answerFor(text) {
  const t = text.toLowerCase();
  for (const entry of KB) {
    if (entry.keywords.some((k) => t.includes(k))) return entry.answer;
  }
  return FALLBACK;
}

const QUICK_REPLIES = [
  { key: "pricing", label: "💰 Pricing?", text: "What is the pricing?" },
  { key: "setup", label: "⏱️ Setup time?", text: "How fast can I go live?" },
  { key: "services", label: "🛠️ Services?", text: "What services are included?" },
  { key: "earnings", label: "📈 Earning potential?", text: "How much extra can I earn?" },
  { key: "contact", label: "📞 Contact?", text: "How can I contact support?" },
];

const AIChatWidget = forwardRef(function AIChatWidget(_props, ref) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      from: "bot",
      text: `Namaste 🙏 I'm Aria — your Digital Onboarding assistant. Ask me about pricing (${STARTER_PRICE} starter), services, setup time, or contact info. I respond in English or Hindi.`,
    },
  ]);
  const [input, setInput] = useState("");
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
    toggle: () => setOpen((v) => !v),
  }));

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const sendText = (text) => {
    if (!text?.trim()) return;
    const clean = text.trim();
    setMessages((m) => [...m, { from: "user", text: clean }]);
    // Static reply with a tiny delay for natural feel
    setTimeout(() => {
      setMessages((m) => [...m, { from: "bot", text: answerFor(clean) }]);
    }, 320);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = input;
    setInput("");
    sendText(text);
  };

  return (
    <>
      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open AI chat"
        className="fixed bottom-24 right-5 z-40 w-14 h-14 rounded-full btn-primary-grad flex items-center justify-center shadow-2xl shadow-violet-500/40 hover:scale-105 transition-transform"
      >
        {open ? <X className="w-6 h-6 text-white" /> : <Bot className="w-6 h-6 text-white" strokeWidth={2.2} />}
        {!open && (
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#05060F]" />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            data-testid="ai-chat-widget"
            className="fixed bottom-40 right-5 z-40 w-[92vw] max-w-sm rounded-2xl glass-strong border border-white/10 shadow-2xl overflow-hidden"
          >
            <div className="p-4 border-b border-white/5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full btn-primary-grad flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-display font-bold text-sm">Aria · AI Assistant</p>
                <p className="text-[11px] text-emerald-400 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Online · bilingual
                </p>
              </div>
            </div>

            <div className="max-h-80 overflow-y-auto p-4 space-y-3 bg-[#05060F]/70">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${i}`}
                  className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.from === "user"
                        ? "btn-primary-grad text-white rounded-br-sm"
                        : "bg-white/[0.04] text-slate-200 border border-white/5 rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <div className="p-3 border-t border-white/5 bg-[#05060F]/85 space-y-2">
              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-1.5" data-testid="chat-suggestions">
                  {QUICK_REPLIES.map((r) => (
                    <button
                      key={r.key}
                      onClick={() => sendText(r.text)}
                      data-testid={`chat-suggestion-${r.key}`}
                      className="px-2.5 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-[11px] text-slate-200 hover:border-violet-500/50 hover:bg-white/[0.08] transition-all"
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 focus-within:border-violet-500/50 transition-colors"
              >
                <input
                  ref={inputRef}
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about pricing, setup, services…"
                  className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 outline-none"
                />
                <button
                  type="submit"
                  data-testid="chat-send"
                  disabled={!input.trim()}
                  className="w-8 h-8 rounded-lg btn-primary-grad flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
                  aria-label="Send"
                >
                  <Send className="w-3.5 h-3.5 text-white" />
                </button>
              </form>

              <p className="text-center text-[10px] text-slate-500">
                Talk to a human: {SUPPORT_PHONE} · {SUPPORT_EMAIL}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});

export default AIChatWidget;
